import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { Branch, BranchContact, BranchPayload } from '../../../types/lists.types';
import { branchesApi } from '../../../utils/lists-api';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    branch: Branch | null;
    users: BranchContact[];
    onSaved: () => void;
}

const EMPTY: BranchPayload = {
    name: '',
    address: '',
    mainContactPersonId: null,
    description: '',
};

export const BranchFormDialog: React.FC<Props> = ({ open, onOpenChange, branch, users, onSaved }) => {
    const [form, setForm] = useState<BranchPayload>(EMPTY);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Guard the render: a contact list that failed to load must not crash the dialog.
    const contactOptions = Array.isArray(users) ? users : [];

    useEffect(() => {
        if (!open) return;
        if (branch) {
            setForm({
                name: branch.name,
                address: branch.address,
                mainContactPersonId: branch.mainContactPerson?.id ?? null,
                description: branch.description ?? '',
            });
        } else {
            setForm(EMPTY);
        }
        setErrors({});
    }, [open, branch]);

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.name.trim()) next.name = 'Branch Name is required';
        if (!form.address.trim()) next.address = 'Address is required';
        if (!form.mainContactPersonId) next.mainContactPersonId = 'Main Contact Person is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);
            if (branch) {
                await branchesApi.update(branch.id, form);
                toast.success('Branch updated');
            } else {
                await branchesApi.create(form);
                toast.success('Branch created');
            }
            onSaved();
            onOpenChange(false);
        } catch (err: any) {
            // api.ts already surfaces the error toast; nothing to add here.
            console.error('Failed to save branch', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium">
                        {branch ? 'Edit Branch' : 'Add Branch'}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        Branches feed the Project List, where only Active branches are selectable.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="branch-name">
                                Branch Name <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                id="branch-name"
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            />
                            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="branch-address">
                                Address <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="branch-address"
                                rows={2}
                                value={form.address}
                                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                            />
                            {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="branch-contact">
                                Main Contact Person <span className="text-destructive">*</span>
                            </Label>
                            <Select
                                value={form.mainContactPersonId ? String(form.mainContactPersonId) : undefined}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, mainContactPersonId: Number(v) }))
                                }
                            >
                                <SelectTrigger id="branch-contact" className="w-full bg-white">
                                    <SelectValue placeholder="Select a person" />
                                </SelectTrigger>
                                <SelectContent>
                                    {contactOptions.length === 0 ? (
                                        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                                            No people available
                                        </div>
                                    ) : (
                                        contactOptions.map((u) => (
                                            <SelectItem key={u.id} value={String(u.id)}>
                                                {u.name}{u.jobRole ? ` — ${u.jobRole}` : ''}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            {errors.mainContactPersonId && (
                                <p className="text-xs text-destructive">{errors.mainContactPersonId}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="branch-description">Description</Label>
                            <Textarea
                                id="branch-description"
                                rows={3}
                                value={form.description || ''}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="gap-2">
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {branch ? 'Save Changes' : 'Add Branch'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BranchFormDialog;
